/**
 * Contentful helpers shared by many pages: the brand-name mapping Contentful filters on, and four
 * renderers that turn a Contentful rich-text field (its `raw` JSON string) into React with the
 * design-system `Typography` and `Link`.
 *
 * Each renderer passes options to `@contentful/rich-text-react-renderer` →
 * `documentToReactComponents`. A node type a renderer doesn't override is drawn by that library's
 * default: plain `<h2>`, `<ol>`, `<blockquote>`, `<table>` and so on. Without an override, an
 * embedded asset renders nothing, an embedded block entry an empty `<div>`, and an inline
 * embedded entry the text `type: embedded-entry-inline id: <entry id>`. So the node types listed
 * on each renderer decide what CMS markup reaches the page with Ryerson styling. All four call
 * `JSON.parse(rawData)`: a missing or malformed `raw` throws.
 *
 * Which renderer each area uses:
 * - {@link renderRichText}: FAQ and glossary (with search highlighting), news articles, location
 *   details, leader profiles, tutorial videos.
 * - {@link renderBasicText}: podcast transcripts (`components/Blog/PodcastDetail.tsx`).
 * - {@link renderCompanyProductsServicesRichText}: Family of Companies products and services
 *   (`components/FamilyOfCompanies/FamilyOfCompaniesRefactor/Company.tsx`).
 * - {@link renderBlogRichText}: no importer. Blog articles render through
 *   `components/Shared/RichText/`.
 *
 * Embedded assets are matched against `references`, the list the `src/contentful/<type>/helpers`
 * build from the entry's links, each carrying the linked asset's `contentful_id` (for example
 * `src/contentful/faq/helpers/index.ts`).
 */
import React from "react";
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types";
import { Typography } from "@ryerson/frontend.typography";
import { Link } from "@ryerson/frontend.navigation";
import { css } from "@emotion/react";
import { Button } from "@ryerson/frontend.button";
import styled from "@emotion/styled";
import { Icon } from "@ryerson/frontend.assets";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { ITheme, useTheme } from "@ryerson/frontend.theme";
import { Media } from "@ryerson/frontend.next";
import { Modal } from "@ryerson/frontend.modal";
import { MakeHighlights } from "@components/Glossary/Page/Page";
import { Video } from "@ryerson/frontend.media";

/**
 * Maps the build's `NEXT_PUBLIC_COMPANY` code to the brand name Contentful uses in its `website`
 * and `company` list fields: `ryerson` → `"Ryerson"`, `southernToolSteel` →
 * `"Southern Tool Steel"`.
 *
 * Any other value, including an empty string, falls back to `"Ryerson"`, so a build with an
 * unrecognised company builds and shows Ryerson's Contentful content. Consumers:
 * `pages/[...path].tsx` → `getStaticPaths` (which `page` entries are built),
 * `components/Contentful/Product.tsx` and `SubProduct.tsx`, and
 * `components/Products/MetalCapabilities/MetalCapabilities.tsx` (which capabilities are shown).
 * The names must match the Contentful field values exactly.
 */
export const companyToCompany = (company: any) => {
	const contentfulCompany: any = {
		ryerson: "Ryerson",
		southernToolSteel: "Southern Tool Steel",
	};
	return contentfulCompany[company] ? contentfulCompany[company] : contentfulCompany.ryerson;
};

const ImageContainer = styled.div`
	display: block;
	width: auto;
	height: auto;
	position: relative;
`;

const IconContainer = styled.div`
	display: block;
	position: absolute;
	top: 7px;
	text-align: center;
	line-height: 28px;
	border-radius: 24px;
	height: 24px;
	width: 24px;
	right: 7px;
`;

/**
 * Mobile image frame: the image with a "scan" icon in its top-right corner that opens the same
 * image in a modal, so a reader can enlarge it.
 */
const ImageWrapper = (props: any) => {
	const { theme } = useTheme();
	const [modalOpen, setModalOpen] = React.useState<boolean>(false);
	return (
		<>
			<ImageContainer>
				<IconContainer
					css={css`
						background-color: rgba(255, 255, 255, 0.8);
					`}
				>
					<Icon
						icon="scan"
						size="xs"
						color={theme.colors.primary.header}
						circled={false}
						selected={false}
						onClick={() => {
							setModalOpen(true);
						}}
					/>
				</IconContainer>
				{props.children ?? ""}
				<Modal
					isOpen={modalOpen}
					closable={true}
					onClose={() => {
						setModalOpen(false);
					}}
				>
					{props.children ?? ""}
				</Modal>
			</ImageContainer>
		</>
	);
};

/**
 * The general-purpose renderer. Overrides bold, italic and underline marks, hyperlinks (design
 * system `Link` to `node.data.uri`), paragraphs (`Typography` `p` with a 21px bottom margin) and
 * embedded assets. Headings, lists, quotes and tables use the library defaults.
 *
 * - `type` is the `Typography` colour type, `"primary"` when absent.
 * - `references`: an embedded asset is drawn only when `references` holds an entry whose
 *   `contentful_id` is the asset's id; otherwise nothing is drawn. Desktop gets a plain `<img>`,
 *   mobile an image that can be enlarged.
 * - `searchTerm`: when longer than two characters, each occurrence in a paragraph's text is
 *   highlighted with `components/Glossary/Page/Page.tsx` → `MakeHighlights`. The FAQ search and
 *   the desktop glossary search pass the term through `components/Utilities/Utilities.tsx` →
 *   `sanitizeInput`; the mobile glossary search box passes it as typed. The term is used as a
 *   regular-expression source, so a term that is an invalid pattern throws when a paragraph
 *   contains it literally: an unclosed `[`, which `sanitizeInput` keeps, or, from the mobile
 *   glossary search box, an unbalanced `(` or a leading `*`.
 */
export const renderRichText = (
	rawData: any,
	type: any,
	references?: any | null,
	searchTerm?: string | null
) => {
	const Bold = ({ children }: any) => <Typography weight="bold">{children}</Typography>;
	const Italics = ({ children }: any) => <Typography italic>{children}</Typography>;
	const Underline = ({ children }: any) => <Typography underline>{children}</Typography>;

	const Text = ({ children }: any) => {
		let newKids: any[] = [];
		const uniqueArrayItems = (value: any, index: any, self: any) => {
			return self.indexOf(value) === index;
		};
		children.forEach((child: any, index: number) => {
			if (searchTerm && searchTerm.length > 2) {
				if (
					typeof child === "string" &&
					child.toLowerCase().includes(searchTerm.toLowerCase())
				) {
					let re = new RegExp(searchTerm, "gi");
					let match = child.match(re) as string[];
					if (match && match.length > 0) {
						match = match.filter(uniqueArrayItems);
						let newChild = child;
						// `|match|` is the marker MakeHighlights splits on; it highlights only
						// markers holding word characters, so a term with a space isn't
						// highlighted. When no marker in the text is recognised, MakeHighlights
						// returns the text with its `|` markers, and they show on the page.
						for (let i = 0; i < match.length; i++) {
							newChild = newChild.replaceAll(match[i], "|" + match[i] + "|");
						}
						newKids.push(
							<React.Fragment key={index}>{MakeHighlights(newChild)}</React.Fragment>
						);
					} else {
						newKids.push(<React.Fragment key={index}>{child}</React.Fragment>);
					}
				} else {
					newKids.push(<React.Fragment key={index}>{child}</React.Fragment>);
				}
			} else {
				newKids.push(<React.Fragment key={index}>{child}</React.Fragment>);
			}
		});
		return (
			<Typography
				variant="p"
				css={css`
					// margin-top: 0px;
					margin-bottom: 21px;
				`}
				type={type ?? "primary"}
			>
				{newKids}
			</Typography>
		);
	};

	const options = {
		renderMark: {
			[MARKS.BOLD]: (text: any) => <Bold>{text}</Bold>,
			[MARKS.ITALIC]: (text: any) => <Italics>{text}</Italics>,
			[MARKS.UNDERLINE]: (text: any) => <Underline>{text}</Underline>,
		},
		renderNode: {
			[INLINES.HYPERLINK]: (node: any, children: any) => (
				<Link to={node.data.uri}>{children}</Link>
			),
			[BLOCKS.PARAGRAPH]: (node: any, children: any) => <Text>{children}</Text>,

			[BLOCKS.EMBEDDED_ASSET]: (node: any, children: any) => {
				if (references) {
					let asset: any;
					references.forEach((ref: any, index: any) => {
						if (ref.contentful_id === node.data.target.sys.id) {
							asset = ref;
						}
					});
					if (asset) {
						return (
							<>
								<Media greaterThanOrEqual="lg">
									<img src={asset.file.url} alt={asset.title} />
								</Media>
								<Media lessThan="lg">
									<ImageWrapper>
										<img src={asset.file.url} alt={asset.title} />
									</ImageWrapper>
								</Media>
							</>
						);
					} else {
						return <></>;
					}
				} else {
					return <></>;
				}
			},
		},
	};
	return documentToReactComponents(JSON.parse(rawData), options);
};

/**
 * Renderer for long plain copy, used for podcast transcripts. Overrides marks, hyperlinks,
 * paragraphs (16px bottom margin), level-2 and level-3 headings, and lists. Ordered lists are
 * drawn as `<ul>`, so they show bullets, not numbers. Embedded assets and entries aren't handled.
 */
export const renderBasicText = (rawData: any, type: any) => {
	const Bold = ({ children }: any) => <Typography weight="bold">{children}</Typography>;
	const Italics = ({ children }: any) => <Typography italic>{children}</Typography>;
	const Underline = ({ children }: any) => <Typography underline>{children}</Typography>;
	const Text = ({ children }: any) => {
		return (
			<Typography
				variant="p"
				css={css`
					margin-bottom: 16px;
					margin-top: 0px;
				`}
				type={type ?? "primary"}
			>
				{children}
			</Typography>
		);
	};
	const options = {
		renderMark: {
			[MARKS.BOLD]: (text: any) => <Bold>{text}</Bold>,
			[MARKS.ITALIC]: (text: any) => <Italics>{text}</Italics>,
			[MARKS.UNDERLINE]: (text: any) => <Underline>{text}</Underline>,
		},
		renderNode: {
			[INLINES.HYPERLINK]: (node: any, children: any) => (
				<Link to={node.data.uri}>{children}</Link>
			),
			[BLOCKS.PARAGRAPH]: (node: any, children: any) => <Text>{children}</Text>,
			[BLOCKS.HEADING_2]: (node: any, children: any) => (
				<Typography
					variant="h2"
					type={type ?? "primary"}
					css={css`
						margin-bottom: 20px;
					`}
				>
					{children}
				</Typography>
			),
			[BLOCKS.HEADING_3]: (node: any, children: any) => (
				<Typography
					variant="h3"
					type={type ?? "primary"}
					css={css`
						margin-bottom: 16px;
					`}
				>
					{children}
				</Typography>
			),
			[BLOCKS.OL_LIST]: (node: any, children: any) => {
				return (
					<ul>
						{children.map((child: any, index: number) => (
							<li key={index}>{child}</li>
						))}
					</ul>
				);
			},
			[BLOCKS.UL_LIST]: (node: any, children: any) => {
				return (
					<ul>
						{children.map((child: any, index: number) => (
							<li key={index}>{child}</li>
						))}
					</ul>
				);
			},
		},
	};
	return documentToReactComponents(JSON.parse(rawData), options);
};

/** An embedded blog image with its asset description as the caption beside it. */
type ImageWithCaption = {
	image: string;
	title?: string;
	caption: string;
	type?: "primary" | "secondary" | "tertiary";
};

const ImageCaptionContainer = styled.div`
	display: block;
	width: calc(100% - 80px);
	height: auto;
	margin-top: 56px;
	margin-bottom: 56px;
	margin-left: 40px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				width: calc(100% - 40px);
				margin-left: 20px;
				margin-top: 42px;
				margin-bottom: 42px;
			}
		`;
	}}
`;

const CaptionedImageContainer = styled.div`
	display: inline-block;
	width: calc(60% - 40px);
	vertical-align: bottom;
	height: auto;
	img {
		width: 100%;
		height: auto;
		display: block;
	}
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				display: block;
				width: 100%;
				margin-bottom: 10px;
			}
		`;
	}}
`;
const CaptionContainer = styled.div`
	display: inline-block;
	width: 40%;
	margin-left: 40px;
	vertical-align: bottom;
	height: auto;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.lg}) {
				display: block;
				width: 100%;
				margin-left: 0px;
			}
		`;
	}}
`;

const CaptionedImage: React.FC<ImageWithCaption> = ({
	image,
	title,
	caption,
	type = "primary",
}) => {
	const { theme } = useTheme();
	return (
		<ImageCaptionContainer theme={theme}>
			<CaptionedImageContainer theme={theme}>
				<Media greaterThanOrEqual="lg">
					<img src={image} alt={title ?? "Image"} />
				</Media>
				<Media lessThan="lg">
					<ImageWrapper>
						<img src={image} alt={title ?? "Image"} />
					</ImageWrapper>
				</Media>
			</CaptionedImageContainer>
			<CaptionContainer theme={theme}>
				<Typography variant="div" type={type} size="sm">
					{caption}
				</Typography>
			</CaptionContainer>
		</ImageCaptionContainer>
	);
};

const VideoContainer = styled.div`
	width: 100%;
	display: block;
	height: 360px;
`;

const ThingLinkContainer = styled.div`
	display: block;
	width: 100%;
	height: auto;
`;

type Infogram = {
	infogramId: string;
	title: string;
};

/**
 * An embedded Infogram chart. On mount it re-scans the page with Infogram's loader when that is
 * already initialised, or else adds the loader script (`id="infogram-async"`, from
 * `e.infogram.com`) once.
 */
const Infogrammer = (props: Infogram) => {
	React.useEffect(() => {
		let t: any = "InfogramEmbeds";
		let d = document.getElementsByTagName("script")[0];
		if ((window as any)[t] && (window as any)[t].initialized)
			(window as any)[t].process && (window as any)[t].process();
		else if (!document.getElementById("infogram-async")) {
			var o = document.createElement("script");
			o.async = true;
			o.id = "infogram-async";
			o.src = "https://e.infogram.com/js/dist/embed-loader-min.js";
			if (d.parentNode) d.parentNode.insertBefore(o, d);
		}
	}, []);
	return (
		<ThingLinkContainer>
			<div
				className="infogram-embed"
				data-id={props.infogramId}
				data-type="interactive"
				data-title={props.title}
			></div>
		</ThingLinkContainer>
	);
};

type ThingLink = {
	thinglinkId: string;
	originalHeight: number;
	originalWidth: number;
};

/**
 * An embedded ThingLink card in an iframe, sized to its container's width with the original
 * aspect ratio, and resized on window resize. The resize listener isn't removed on unmount.
 */
const ThingLinker = (props: ThingLink) => {
	const isWindow = typeof window !== "undefined";
	const thinglinkId = props.thinglinkId;
	const aspect = props.originalHeight / props.originalWidth;
	const [thingH, setThingH] = React.useState<number>(props.originalHeight);
	const [thingW, setThingW] = React.useState<number>(props.originalWidth);
	React.useEffect(() => {
		if (isWindow) {
			if (document && document.getElementById(thinglinkId) !== null) {
				let width = document.getElementById(thinglinkId)?.offsetWidth;
				if (width) {
					setThingW(width);
					let height = width * aspect;
					setThingH(height);
				}
			}
			window.addEventListener("resize", () => {
				if (document && document.getElementById(thinglinkId) !== null) {
					let width = document.getElementById(thinglinkId)?.offsetWidth;
					if (width) {
						setThingW(width);
						let height = width * aspect;
						setThingH(height);
					}
				}
			});
		}
	}, []);
	return (
		<ThingLinkContainer id={props.thinglinkId}>
			<iframe
				width={thingW}
				height={thingH}
				data-original-width={props.originalWidth}
				data-original-height={props.originalHeight}
				css={css`
					border: none;
				`}
				scrolling="no"
				src={"https://www.thinglink.com/card/" + props.thinglinkId}
			></iframe>
		</ThingLinkContainer>
	);
};

/**
 * The blog-article renderer. No importer in this repo: blog articles render through
 * `components/Shared/RichText/`.
 *
 * Beyond marks and paragraphs it handles:
 * - hyperlinks whose text contains `[pdf]`: the placeholder is removed and the link is drawn as a
 *   PDF button; any other `[...]` placeholder leaves the text as it is;
 * - inline embedded entries with a `videoId` (video), `thinglinkId` (ThingLink) or `infogramId`
 *   (Infogram), matched against `references` by `contentful_id`;
 * - quotes, where a paragraph whose text contains `[` and `]` loses its first `[` and `]` and
 *   becomes a large pull-quote, and the other paragraphs stay as body text;
 * - level-2 to level-4 headings, embedded images (with the asset description as a caption when
 *   it has one; matched when the asset's file URL contains the target id), and tables with
 *   striped rows.
 */
export const renderBlogRichText = (
	rawData: any,
	type: any,
	theme: ITheme,
	references?: any | null
) => {
	const Bold = ({ children }: any) => <Typography weight="bold">{children}</Typography>;
	const Italics = ({ children }: any) => <Typography italic>{children}</Typography>;
	const Underline = ({ children }: any) => <Typography underline>{children}</Typography>;
	const options = {
		renderMark: {
			[MARKS.BOLD]: (text: any) => <Bold>{text}</Bold>,
			[MARKS.ITALIC]: (text: any) => <Italics>{text}</Italics>,
			[MARKS.UNDERLINE]: (text: any) => <Underline>{text}</Underline>,
		},
		renderNode: {
			[INLINES.HYPERLINK]: (node: any, children: any) => {
				let ele;
				if (typeof children[0] === "string") {
					let content = children[0];
					if (content.includes("[") && content.includes("]")) {
						let match = content.match("\\[.*]");
						if (match && match.length > 0) {
							let placeholder = match[0];
							content = content.replace(placeholder, "");
							switch (placeholder) {
								case "[pdf]":
									ele = (
										<span
											css={css`
												display: block;
												margin: 36px 0px 64px;
												@media (max-width: ${theme.breakpoints.lg}) {
													margin: 24px 0px 44px;
												}
											`}
										>
											<Button
												shape="rounded"
												size="xs"
												leftIcon="file-pdf"
												paddingType="relaxed"
												type="secondary"
												foreground="light"
												label={content}
												onClick={() => {}}
											/>
										</span>
									);
									break;
								default:
									ele = children[0];
									break;
							}
						} else {
							ele = children[0];
						}
					} else {
						ele = children[0];
					}
				} else {
					ele = children[0];
				}
				return <Link to={node.data.uri}>{ele}</Link>;
			},
			[INLINES.EMBEDDED_ENTRY]: (node: any, children: any) => {
				if (references) {
					let asset: any;
					references.forEach((ref: any, index: any) => {
						if (ref.contentful_id === node.data.target.sys.id) {
							asset = ref;
						}
					});
					if (asset) {
						if (asset.videoId) {
							return (
								<>
									<Media greaterThanOrEqual="lg">
										<VideoContainer>
											<Video
												videoId={asset.videoId}
												buttonOrientation="right"
												imageUrl={asset.thumbnail.file.url}
												text={asset.title}
											/>
										</VideoContainer>
									</Media>
									<Media lessThan="lg">
										<VideoContainer
											css={css`
												height: 240px;
												margin-top: 40px;
											`}
										>
											<Video
												videoId={asset.videoId}
												buttonOrientation="top-right"
												imageUrl={asset.thumbnail.file.url}
												text={asset.title}
											/>
										</VideoContainer>
									</Media>
								</>
							);
						} else if (asset.thinglinkId) {
							return (
								<ThingLinker
									thinglinkId={asset.thinglinkId}
									originalHeight={asset.originalHeight}
									originalWidth={asset.originalWidth}
								/>
							);
						} else if (asset.infogramId) {
							return (
								<Infogrammer infogramId={asset.infogramId} title={asset.title} />
							);
						} else {
							return <></>;
						}
					} else {
						return <></>;
					}
				} else {
					return <></>;
				}
			},
			[BLOCKS.PARAGRAPH]: (node: any, children: any) => (
				<Typography
					css={css`
						white-space: pre-line;
					`}
					variant="p"
					type={type ?? "primary"}
				>
					{children}
				</Typography>
			),
			[BLOCKS.HEADING_2]: (node: any, children: any) => (
				<Typography
					css={css`
						font-size: 20px;
						line-height: 25px;
						letter-spacing: -1px;
					`}
					variant="h2"
					type={type ?? "primary"}
				>
					{children}
				</Typography>
			),
			[BLOCKS.HEADING_3]: (node: any, children: any) => (
				<Typography
					css={css`
						font-size: 16px;
						line-height: 25px;
						letter-spacing: -1px;
					`}
					variant="h3"
					type={type ?? "primary"}
				>
					{children}
				</Typography>
			),
			[BLOCKS.HEADING_4]: (node: any, children: any) => (
				<Typography
					variant="h4"
					css={css`
						font-size: 16px;
						font-weight: 500;
						font-family: ${theme.typography.fontFamilies.primary};
						line-height: 20px;
						@media (max-width: ${theme.breakpoints.lg}) {
							font-size: 14px;
						}
					`}
					type={type ?? "primary"}
				>
					{children}
				</Typography>
			),
			[BLOCKS.QUOTE]: (node: any, children: any) => {
				let segmentedContent: any[] = [];
				children.forEach((child: any, index: number) => {
					if (child.props.children && child.props.children.length > 0) {
						if (typeof child.props.children[0] === "string") {
							let content = child.props.children[0] ?? "";
							if (content.includes("[") && content.includes("]")) {
								//This is the quote
								content = content.replace("[", "");
								content = content.replace("]", "");
								let quote = (
									<Typography
										key={index}
										variant="h3"
										type="tertiary"
										css={css`
											margin-bottom: 36px;
											letter-spacing: -1px;
											position: relative;
											margin-top: 0px;
											max-width: 560px;
											white-space: normal;
											@media (max-width: ${theme.breakpoints.lg}) {
												white-space: normal;
												font-size: 20px;
												line-height: 24px;
												margin-bottom: 24px;
											}
										`}
									>
										<span
											css={css`
												position: absolute;
												left: -15px;
												display: block;
											`}
										>
											“
										</span>
										{content}
										<span css={css`display;inline-block; margin-left: 7px;`}>
											”
										</span>
									</Typography>
								);
								segmentedContent.push(quote);
							} else {
								segmentedContent.push(child);
							}
						} else {
							segmentedContent.push(child);
						}
					}
				});
				return (
					<Typography
						variant="div"
						size="sm"
						css={css`
							background-color: ${theme.colors.primary.gray};
							p {
								color: ${theme.colors.tertiary.header} !important;
								white-space: pre-line;
								margin: 0;
							}
							p span {
								color: ${theme.colors.tertiary.header} !important;
								white-space: pre-line;
							}
							padding: 46px 65px 50px 65px;
							box-sizing: border-box;
							margin-top: 54px;
							margin-bottom: 56px;
							@media (max-width: ${theme.breakpoints.lg}) {
								padding: 30px 22px 32px 36px;
								margin-top: 34px;
								margin-bottom: 32px;
							}
						`}
					>
						{segmentedContent}
					</Typography>
				);
			},
			[BLOCKS.EMBEDDED_ASSET]: (node: any, children: any) => {
				if (references) {
					let asset: any;
					references.forEach((ref: any, index: any) => {
						if (
							ref &&
							ref.file &&
							ref.file.url &&
							ref.file.url.includes(node.data.target.sys.id)
						) {
							asset = ref;
						}
					});
					if (asset) {
						return (
							<>
								<Media greaterThanOrEqual="lg">
									{asset.description && asset.description.length > 0 ? (
										<CaptionedImage
											title={asset.title}
											caption={asset.description}
											type={type ?? "primary"}
											image={asset.file.url}
										/>
									) : (
										<img
											css={css`
												display: block;
												margin-left: 40px;
												margin-top: 56px;
												margin-bottom: 56px;
												max-width: calc(100% - 80px);
											`}
											src={asset.file.url}
											alt={asset.title}
										/>
									)}
								</Media>
								<Media lessThan="lg">
									{asset.description && asset.description.length > 0 ? (
										<CaptionedImage
											title={asset.title}
											caption={asset.description}
											type={type ?? "primary"}
											image={asset.file.url}
										/>
									) : (
										<ImageWrapper>
											<img
												src={asset.file.url}
												css={css`
													max-width: calc(100% - 40px);
													margin-left: 20px;
													margin-top: 34px;
													margin-bottom: 34px;
													display: block;
												`}
												alt={asset.title}
											/>
										</ImageWrapper>
									)}
								</Media>
							</>
						);
					} else {
						return <></>;
					}
				} else {
					return <></>;
				}
			},
			[BLOCKS.TABLE]: (node: any, children: any) => {
				return (
					<div
						css={css`
							max-width: calc(100% - 80px);
							height: auto;
							display: block;
							overflow-x: auto;
							overflow-y: hidden;
							margin-left: 40px;
							border-radius: 3px;
							border-top: 3px solid ${theme.colors.tertiary.background};
							border-bottom: 3px solid ${theme.colors.tertiary.background};
							@media (max-width: ${theme.breakpoints.lg}) {
								margin-left: 20px;
								max-width: calc(100% - 40px);
							}
						`}
					>
						<table
							css={css`
								border-collapse: collapse;
								box-sizing: border-box;
								min-width: 100%;
								tr:nth-of-type(odd) {
									background-color: ${theme.colors.primary.background};
								}
								tr:nth-of-type(even) {
									background-color: ${theme.colors.secondary.background};
								}
							`}
						>
							<tbody>{children}</tbody>
						</table>
					</div>
				);
			},

			[BLOCKS.TABLE_ROW]: (node: any, children: any) => {
				return <tr>{children}</tr>;
			},
			[BLOCKS.TABLE_HEADER_CELL]: (node: any, children: any) => {
				return (
					<th
						css={css`
							background-color: ${theme.colors.tertiary.background};
							box-sizing: border-box;
							padding: 5px;
							border-bottom: 1px solid ${theme.colors.tertiary.background};
							text-align: left;
						`}
					>
						<Typography
							variant="span"
							size="sm"
							css={css`
								p {
									margin: 0px;
									color: ${theme.colors.tertiary.header} !important;
								}
							`}
						>
							{children}
						</Typography>
					</th>
				);
			},
			[BLOCKS.TABLE_CELL]: (node: any, children: any) => {
				return (
					<td
						css={css`
							box-sizing: border-box;
							padding: 5px;
							border-bottom: 1px solid ${theme.colors.primary.lighterGray};
						`}
					>
						<Typography
							variant="span"
							size="sm"
							type="primary"
							css={css`
								p {
									margin: 0px;
									color: ${theme.colors.primary.text} !important;
								}
							`}
						>
							{children}
						</Typography>
					</td>
				);
			},
		},
	};
	return documentToReactComponents(JSON.parse(rawData), options);
};

/**
 * Renderer for a Family of Companies company's products and services fields
 * (`components/FamilyOfCompanies/FamilyOfCompaniesRefactor/Company.tsx`). Overrides marks,
 * hyperlinks, small paragraphs that keep line breaks, lists, and tables laid out at full width
 * with fixed columns. Ordered lists are drawn as `<ul>`, so they show bullets, not numbers.
 * Embedded assets and entries aren't handled.
 */
export const renderCompanyProductsServicesRichText = (rawData: any, type: any, theme: ITheme) => {
	const Bold = ({ children }: any) => <Typography weight="bold">{children}</Typography>;
	const Italics = ({ children }: any) => <Typography italic>{children}</Typography>;
	const Underline = ({ children }: any) => <Typography underline>{children}</Typography>;
	const options = {
		renderMark: {
			[MARKS.BOLD]: (text: any) => <Bold>{text}</Bold>,
			[MARKS.ITALIC]: (text: any) => <Italics>{text}</Italics>,
			[MARKS.UNDERLINE]: (text: any) => <Underline>{text}</Underline>,
		},
		renderNode: {
			[INLINES.HYPERLINK]: (node: any, children: any) => (
				<Link to={node.data.uri}>{children}</Link>
			),
			[BLOCKS.PARAGRAPH]: (node: any, children: any) => (
				<Typography
					css={css`
						white-space: pre-line;
					`}
					variant="p"
					size="sm"
					type={type ?? "primary"}
				>
					{children}
				</Typography>
			),
			[BLOCKS.OL_LIST]: (node: any, children: any) => {
				return (
					<ul>
						{children.map((child: any, index: number) => (
							<li key={index}>{child}</li>
						))}
					</ul>
				);
			},
			[BLOCKS.UL_LIST]: (node: any, children: any) => {
				return (
					<ul>
						{children.map((child: any, index: number) => (
							<li key={index}>{child}</li>
						))}
					</ul>
				);
			},
			[BLOCKS.TABLE]: (node: any, children: any) => {
				return (
					<div
						css={css`
							// max-width: calc(100% - 80px);
							height: auto;
							display: block;
							overflow-x: auto;
							overflow-y: hidden;
							// margin-left: 40px;
							width: 100%;
						`}
					>
						<table
							css={css`
								border-collapse: collapse;
								box-sizing: border-box;
								min-width: 100%;
								table-layout: fixed;
								width: 100%;
							`}
						>
							<tbody>{children}</tbody>
						</table>
					</div>
				);
			},

			[BLOCKS.TABLE_ROW]: (node: any, children: any) => {
				return <tr>{children}</tr>;
			},
			[BLOCKS.TABLE_HEADER_CELL]: (node: any, children: any) => {
				return (
					<th
						css={css`
							box-sizing: border-box;
							padding: 5px;
							text-align: left;
							max-width: 50%;
							min-width: 33%;
							overflow: hidden;
						`}
					>
						<Typography
							variant="span"
							size="sm"
							css={css`
								p {
									// margin: 0px;
									// color: ${theme.colors.tertiary.header} !important;
								}
							`}
						>
							{children}
						</Typography>
					</th>
				);
			},
			[BLOCKS.TABLE_CELL]: (node: any, children: any) => {
				return (
					<td
						css={css`
							box-sizing: border-box;
							padding: 5px;
							max-width: 50%;
							min-width: 33%;
							overflow: hidden;
						`}
					>
						<Typography
							variant="span"
							size="sm"
							type="primary"
							css={css`
								p {
									// margin: 0px;
									color: ${theme.colors.primary.text} !important;
								}
							`}
						>
							{children}
						</Typography>
					</td>
				);
			},
		},
	};
	return documentToReactComponents(JSON.parse(rawData), options);
};
